// pages/Companies.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import api from '../utils/apiInterceptor';
import { apiService } from '../services/apiService';
import SearchHistory from '../components/SearchHistory';
import { useSearchHistory } from '../hooks/useSearchHistory';
import { useApiData } from '../hooks/useApiData';
import { useHistoryHandler } from '../hooks/useHistoryHandler';
import { useFilters } from '../hooks/useFilters';
import { API_URL } from '../constants/data';
import Tooltip from '@mui/material/Tooltip';
import ReactApexChart from 'react-apexcharts';
import CompanyFilterPanel from '../components/CompanyFilterPanel';

import {
  Box, CircularProgress, Alert,
  Chip,
  Collapse,
  Fade,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Toolbar as MuiToolbar,
  Stack,
  TableSortLabel,
  TablePagination,
  LinearProgress,
  FormControl,
  Select,
  MenuItem,
  Divider,
  Snackbar,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FileDownload as FileDownloadIcon,
  ClearAll as ClearAllIcon,
  Visibility as VisibilityIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowRight as ArrowRightIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocalStorage } from '../hooks/useLocalStorage';

const CACHE_EXPIRATION = 24 * 60 * 60 * 1000;

// ─── Rating metadata ─────────────────────────────────────────────────────────
const RATING_META = [
  { key: '5', label: 'Excellent', color: '#2e7d32' },
  { key: '4', label: 'Very Good', color: '#558b2f' },
  { key: '3', label: 'Good', color: '#f9a825' },
  { key: '2', label: 'Poor', color: '#e65100' },
  { key: '1', label: 'Very Bad', color: '#c62828' },
  { key: '0', label: 'No Rating', color: '#9e9e9e' },
];

// ─── Expand Panel ─────────────────────────────────────────────────────────────
const ExpandPanel = ({ row }) => {
  const categories = Array.isArray(row.categories) ? row.categories : [];
  const feedbackDist = row.feedbackDistribution || {};

  // ── Category bar chart ──────────────────────────────────────────────────
  const catSeries = [{ name: 'Complaints', data: categories.map(c => c.count || 0) }];
  const catColors = [
    '#D0CEFB', '#FEB4AA', '#B8DFFE', '#FCE7B8', '#FCD4E3',
    '#C5E1A5', '#FFE082', '#FFAB91', '#CE93D8', '#90CAF9',
    '#A5D6A7', '#FFCC80', '#EF9A9A', '#B39DDB', '#81D4FA',
    '#C5E1A5', '#FFD54F', '#BCAAA4', '#E1BEE7', '#80DEEA',
    '#DCEDC8', '#FFF59D', '#FFCCBC', '#D1C4E9', '#B2EBF2',
  ];
  const catOptions = {
    chart: { type: 'bar', toolbar: { show: false }, animations: { enabled: true, speed: 600 } },
    plotOptions: {
      bar: {
        horizontal: true,
        distributed: true,
        borderRadius: 3,
        barHeight: categories.length > 8 ? '50%' : categories.length > 4 ? '60%' : '70%',
        dataLabels: { position: 'top' },
      },
    },
    colors: categories.map((_, i) => catColors[i % catColors.length]),
    dataLabels: {
      enabled: true,
      offsetX: 28,
      style: { fontSize: '10px', fontWeight: 600, colors: ['#333'] },
      formatter: val => val.toLocaleString(),
    },
    xaxis: {
      categories: categories.map(c => c.name || ''),
      labels: { show: false },
    },
    yaxis: {
      labels: {
        show: true,
        trim: false,
        style: { fontSize: '11px', fontWeight: 500, colors: '#444' },
        maxWidth: 260,
      },
    },
    legend: { show: false },
    tooltip: {
      theme: 'light',
      y: { formatter: val => val.toLocaleString(), title: { formatter: () => 'Count: ' } },
    },
    grid: { show: false, xaxis: { lines: { show: false } } },
  };

  // ── Feedback donut chart ────────────────────────────────────────────────
  const activeFeedback = RATING_META.filter(r => (feedbackDist[r.key] || 0) > 0);
  const hasFeedback = activeFeedback.length > 0;

  const donutSeries = activeFeedback.map(r => feedbackDist[r.key] || 0);
  const donutLabels = activeFeedback.map(r => r.label);
  const donutColors = activeFeedback.map(r => r.color);
  const totalFeedback = donutSeries.reduce((s, v) => s + v, 0);

  const donutOptions = {
    chart: { type: 'donut', animations: { enabled: true, speed: 600 } },
    labels: donutLabels,
    colors: donutColors,
    legend: {
      position: 'bottom',
      fontSize: '10px',
      itemMargin: { horizontal: 4, vertical: 2 },
      markers: { width: 10, height: 10, radius: 2 },
    },
    dataLabels: {
      enabled: true,
      formatter: val => val.toFixed(0) + '%',
      style: { fontSize: '10px', fontWeight: 600, colors: ['#fff'] },
      dropShadow: { enabled: false },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              fontSize: '11px',
              fontWeight: 600,
              color: '#444',
              formatter: () => totalFeedback.toString(),
            },
          },
        },
      },
    },
    tooltip: {
      theme: 'light',
      y: {
        formatter: (val) => {
          const pct = totalFeedback > 0 ? ((val / totalFeedback) * 100).toFixed(1) : 0;
          return `${val} (${pct}%)`;
        },
      },
    },
    stroke: { width: 2 },
  };

  const catHeight = Math.max(220, categories.length * 36);
  const panelMinHeight = 280;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        p: 2,
        minHeight: panelMinHeight,
        backgroundColor: '#f8f9ff',
        borderTop: '1px solid #e8e8f0',
        borderBottom: '2px solid #e0e0f0',
      }}
    >
      {/* ── Category Distribution (Bar) ─────────────────────────────── */}
      <Box sx={{ flex: 1.6, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontWeight: 700,
            fontSize: '0.72rem',
            color: '#555',
            textTransform: 'uppercase',
            letterSpacing: 0.8,
            mb: 0.5,
          }}
        >
          Category Distribution
        </Typography>
        {categories.length > 0 ? (
          <Box sx={{
            height: 300,
            overflowY: 'auto',
            overflowX: 'hidden',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.15)',
              borderRadius: '3px',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.25)',
              },
            },
          }}>
            <ReactApexChart
              options={catOptions}
              series={catSeries}
              type="bar"
              height={catHeight}
              width="100%"
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: panelMinHeight - 40, color: 'text.disabled', fontSize: '0.85rem' }}>
            No category data available
          </Box>
        )}
      </Box>

      <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, borderColor: '#dde' }} />

      {/* ── Feedback Distribution (Donut) ───────────────────────────── */}
      <Box sx={{ flex: 1, minWidth: 300, display: 'flex', flexDirection: 'column', minHeight: panelMinHeight - 40 }}>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontWeight: 700,
            fontSize: '0.72rem',
            color: '#555',
            textTransform: 'uppercase',
            letterSpacing: 0.8,
            mb: 0.5,
          }}
        >
          Feedback Distribution
        </Typography>
        {hasFeedback ? (
          <ReactApexChart
            options={donutOptions}
            series={donutSeries}
            type="donut"
            height={300}
            width="100%"
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: 'text.disabled', fontSize: '0.85rem', minHeight: panelMinHeight - 60 }}>
            No feedback data available
          </Box>
        )}
      </Box>

      {/* ── Key Stats strip ─────────────────────────────────────────── */}
      <Box
        sx={{
          display: { xs: 'none', lg: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 1.5,
          minWidth: 130,
          pl: 1,
          borderLeft: '1px solid #dde',
        }}
      >
        {[
          { label: 'Total', value: (row.count || 0).toLocaleString(), color: '#333' },
          { label: 'Disposed', value: (row.disposedCount || 0).toLocaleString(), color: '#333' },
          {
            label: 'Disposal Rate',
            value: row.disposalRate ? `${row.disposalRate.toFixed(1)}%` : '—',
            color: row.disposalRate >= 90 ? '#2e7d32' : row.disposalRate >= 70 ? '#ed6c02' : '#d32f2f',
          },
          {
            label: 'Avg. Days',
            value: row.avgDisposalDays != null ? `${row.avgDisposalDays.toFixed(1)} d` : '—',
            color: row.avgDisposalDays <= 15 ? '#2e7d32' : row.avgDisposalDays <= 30 ? '#ed6c02' : '#d32f2f',
          },
          {
            label: 'category Counts',
            value: row.categories.length != null ? `${row.categories.length}` : '—',
            color: '#ed6c02',
          },
        ].map(stat => (
          <Box key={stat.label}>
            <Typography sx={{ fontSize: '0.65rem', color: '#888', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {stat.label}
            </Typography>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: stat.color }}>
              {stat.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};


const Companies = () => {
  const [companiesWithCounts, setCompaniesWithCounts] = useState([]);
  const [companyData, setCompanyData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterField, setFilterField] = useState('companyName');
  const [orderBy, setOrderBy] = useState('counts');
  const [order, setOrder] = useState('desc');
  const [companyType, setCompanyType] = useState('all');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [viewSnackbarOpen, setViewSnackbarOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const hasAutoFetched = useRef(false);
  const lastFetchParams = useRef(null);

  const [currentQuery, setCurrentQuery] = useState('');
  const [complaintNumbers, setComplaintNumbers] = useState(['NA']);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);

  const { setTotalCounts, fetchSemanticRCA, fetchUserData, fetchSpatialData, setIsLoading } = useApiData();
  const { searchHistory, saveToHistory, clearHistory, deleteHistoryItem, selectedHistoryIndex, setSelectedHistoryIndex } = useSearchHistory();
  const { filters, updateFilters } = useFilters();
  const { handleHistoryClick } = useHistoryHandler({ fetchSemanticRCA, fetchSpatialData, fetchUserData, setTotalCounts, setIsLoading });

  const [companyFilters, setCompanyFilters] = useState({
    query: filters.query || '',
    startDate: filters.startDate ? new Date(filters.startDate) : new Date(new Date().getFullYear() - 1, 0, 1),
    endDate: filters.endDate ? new Date(filters.endDate) : new Date(),
    threshold: 1.3
  });

  useEffect(() => {
    setCompanyFilters(prev => ({
      ...prev,
      query: filters.query || prev.query,
      startDate: filters.startDate ? new Date(filters.startDate) : prev.startDate,
      endDate: filters.endDate ? new Date(filters.endDate) : prev.endDate,
    }));
  }, [filters.query, filters.startDate, filters.endDate]);

  // Reset to root-level data (no query, default complaints)
  const resetToRoot = useCallback(() => {
    setCurrentQuery('');
    setComplaintNumbers(['NA']);
    setPage(0);
    lastFetchParams.current = null; // force re-fetch
    updateFilters({ query: '', complaint_numbers: ['NA'] });
  }, [updateFilters]);

  const handleClearHistory = useCallback(() => {
    clearHistory();
    resetToRoot();
  }, [clearHistory, resetToRoot]);

  const handleDeleteHistoryItem = useCallback((item) => {
    deleteHistoryItem(item);
    // If no history items remain after deletion, reset to root
    if (searchHistory?.length <= 1) {
      resetToRoot();
    }
  }, [deleteHistoryItem, searchHistory, resetToRoot]);

  const loadPageData = useCallback(async (pageNum = 0, pageSize = 15, forceRefresh = false) => {
    const fetchKey = JSON.stringify({ page: pageNum, size: pageSize, query: currentQuery, complaints: complaintNumbers, filters });
    if (lastFetchParams.current === fetchKey && !forceRefresh) return;
    lastFetchParams.current = fetchKey;
    setIsPageLoading(true);
    setError(null);
    try {
      const activeFilters = {
        ...filters,
        query: currentQuery || filters.query || '',
        complaintNumbers: complaintNumbers.length > 0 && complaintNumbers[0] !== 'NA'
          ? complaintNumbers
          : (filters.complaint_numbers || ['NA']),
      };
      const skip = pageNum * pageSize;

      let fetchedData = null;
      const SUB_COMPANY_CACHE_KEY = "subCompanyCache";

      // 1. Check cache ONLY IF not forceRefresh and first page
      if (skip === 0 && !forceRefresh) {
        try {
          const cached = JSON.parse(localStorage.getItem(SUB_COMPANY_CACHE_KEY) || "{}");
          const isCacheValid = (timestamp) => {
            if (!timestamp) return false;
            return (Date.now() - timestamp) / (1000 * 60 * 60) < 24;
          };

          if (cached?.prompt === activeFilters.query &&
            cached?.timestamp &&
            isCacheValid(cached.timestamp) &&
            cached?.data) {
            fetchedData = cached.data;
          }
        } catch (error) {
          console.error('Error reading company cache:', error);
        }
      }

      // 2. Fetch from API if data not found in cache or forceRefresh
      if (!fetchedData) {
        const start_date = companyFilters.startDate
          ? new Date(companyFilters.startDate).toISOString().split('T')[0]
          : activeFilters.startDate ? new Date(activeFilters.startDate).toISOString().split('T')[0] : new Date(new Date().getFullYear() - 1, 0, 1).toISOString().split('T')[0];
        const end_date = companyFilters.endDate
          ? new Date(companyFilters.endDate).toISOString().split('T')[0]
          : activeFilters.endDate ? new Date(activeFilters.endDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];

        const response = await api.post(
          `${API_URL}/get_all_company_distribution`,
          {
            query: forceRefresh ? companyFilters.query : (activeFilters.query || ""),
            start_date: start_date,
            end_date: end_date,
            purchaseCity: 'All',
            complaintMode: 'All',
            complaintStatus: 'All',
            stateName: 'All',
            value: activeFilters.value || 1,
            companyName: "All",
            threshold: forceRefresh ? companyFilters.threshold : 1.3,
            complainNumbers: activeFilters.complaintNumbers || ["NA"],
            skip: skip,
            size: pageSize,
          }
        );

        fetchedData = response.data;

        // 3. Update Cache ONLY IF not forceRefresh and first page
        // We explicitly avoid writing cache when filters are applied (forceRefresh)
        // so that dashboard data isn't overwritten
        if (skip === 0 && !forceRefresh) {
          try {
            localStorage.setItem(SUB_COMPANY_CACHE_KEY, JSON.stringify({
              prompt: activeFilters.query,
              timestamp: Date.now(),
              data: response.data
            }));
          } catch (error) {
            console.error('Error writing company cache:', error);
          }
        }
      }

      let companies = {};
      if (fetchedData && typeof fetchedData === 'object' && !Array.isArray(fetchedData)) {
        companies = fetchedData || {};
      } else if (Array.isArray(fetchedData)) {
        companies = fetchedData;
      }
      setCompanyData(companies);
    } catch (err) {
      console.error('Failed to fetch company distribution:', err);
      setError('Failed to load company data');
      setCompanyData({});
    } finally {
      setIsPageLoading(false);
    }
  }, [filters, currentQuery, complaintNumbers, companyFilters]);

  useEffect(() => { loadPageData(page, rowsPerPage); }, [page, rowsPerPage, currentQuery, complaintNumbers, loadPageData]);

  useEffect(() => {
    const autoFetch = async () => {
      if (hasAutoFetched.current) return;
      let queryToUse = '';
      let complaintNumbersToUse = ['NA'];
      if (location.state?.autoFetch && searchHistory?.length > 0) {
        const lastQuery = searchHistory[searchHistory.length - 1];
        queryToUse = lastQuery?.params?.query || lastQuery?.query || '';
        complaintNumbersToUse = lastQuery?.params?.complaint_numbers || ['NA'];
      }
      try {
        const cached = JSON.parse(localStorage.getItem('currentSubcategoryData') || '{}');
        if (cached?.prompt && !queryToUse && Date.now() - (cached.timestamp || 0) < 1000 * 60 * 60 * 24) {
          queryToUse = cached.prompt;
          if (cached.complaintNumbers) complaintNumbersToUse = cached.complaintNumbers;
        }
      } catch (e) { console.warn('Cache check failed', e); }
      if (queryToUse) {
        setPage(0);
        setCurrentQuery(queryToUse);
        setComplaintNumbers(complaintNumbersToUse);
        updateFilters({ query: queryToUse, complaint_numbers: complaintNumbersToUse });
      }
      hasAutoFetched.current = true;
    };
    autoFetch();
  }, [searchHistory, location.state, updateFilters]);

  useEffect(() => {
    let dataList = [];
    if (companyType === 'all') {
      const conv = (companyData['converged'] || []).map(r => ({ ...r, _type: 'converged' }));
      const nonConv = (companyData['non_converged'] || []).map(r => ({ ...r, _type: 'non_converged' }));
      dataList = [...conv, ...nonConv];
    } else {
      dataList = (companyData[companyType] || []).map(r => ({ ...r, _type: companyType }));
    }
    setCompaniesWithCounts(dataList);
    setExpandedRows(new Set()); // collapse all on data change
    const skip = page * rowsPerPage;
    if (dataList.length < rowsPerPage) { setHasMore(false); setTotalCount(skip + dataList.length); }
    else { setHasMore(true); setTotalCount((page + 2) * rowsPerPage); }
  }, [companyData, companyType, page, rowsPerPage]);

  const filteredData = useMemo(() => {
    if (!searchTerm) return companiesWithCounts;
    const q = searchTerm.toLowerCase();
    return companiesWithCounts.filter(row =>
      (row.companyName || '').toString().toLowerCase().includes(q) ||
      (row.categoryName || '').toString().toLowerCase().includes(q) ||
      (row.sectorName || '').toString().toLowerCase().includes(q)
    );
  }, [companiesWithCounts, searchTerm]);

  const sortedData = useMemo(() => {
    const sorted = [...filteredData].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];
      if (orderBy === 'counts' || orderBy === 'count') { aValue = a.count || a.counts || 0; bValue = b.count || b.counts || 0; }
      if (typeof aValue === 'number' && typeof bValue === 'number') return order === 'asc' ? aValue - bValue : bValue - aValue;
      aValue = (aValue == null ? '' : String(aValue)).toLowerCase();
      bValue = (bValue == null ? '' : String(bValue)).toLowerCase();
      return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
    return sorted;
  }, [filteredData, orderBy, order]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 25)); setPage(0); };
  useEffect(() => { setPage(0); }, [searchTerm]);

  const rowKey = (row, index) => `${row.companyName}-${row._type}-${index}`;
  const allExpanded = sortedData.length > 0 && sortedData.every((row, i) => expandedRows.has(rowKey(row, i)));

  const toggleRow = (key) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    if (allExpanded) {
      setExpandedRows(new Set());
    } else {
      setExpandedRows(new Set(sortedData.map((row, i) => rowKey(row, i))));
    }
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const activeFilters = {
        ...filters,
        query: currentQuery || filters.query || '',
      };
      const start_date = companyFilters.startDate
        ? new Date(companyFilters.startDate).toISOString().split('T')[0]
        : activeFilters.startDate ? new Date(activeFilters.startDate).toISOString().split('T')[0] : new Date(new Date().getFullYear() - 1, 0, 1).toISOString().split('T')[0];
      const end_date = companyFilters.endDate
        ? new Date(companyFilters.endDate).toISOString().split('T')[0]
        : activeFilters.endDate ? new Date(activeFilters.endDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];

      const response = await api.post(
        `${API_URL}/get_all_company_distribution`,
        {
          query: companyFilters.query || currentQuery || "",
          start_date: start_date,
          end_date: end_date,
          purchaseCity: 'All',
          complaintMode: 'All',
          complaintStatus: 'All',
          stateName: 'All',
          value: activeFilters.value || 1,
          companyName: "All",
          threshold: companyFilters.threshold || 1.3,
          complainNumbers: complaintNumbers || activeFilters.complaint_numbers || ["NA"],
          skip: 0,
          size: 1000,
        }
      );

      let mergedDistribution = [];
      if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        Object.values(response.data).forEach(arr => {
          if (Array.isArray(arr)) {
            mergedDistribution = mergedDistribution.concat(arr);
          }
        });
      } else if (Array.isArray(response.data)) {
        mergedDistribution = response.data;
      }

      const data = mergedDistribution;
      let allData = [];
      if (data && typeof data === 'object' && !Array.isArray(data)) allData = data[companyType] || [];
      else if (Array.isArray(data)) allData = data;
      const csvRows = [
        ['Company Name', 'Category', 'Sector', 'Complaints Count', 'Disposed Count', 'Avg. Disposal Days'],
        ...allData.map(row => [
          row.companyName, row.categoryName, row.sectorName,
          row.count || row.counts, row.disposedCount || 0,
          row.avgDisposalDays != null ? row.avgDisposalDays.toFixed(1) : '',
        ]),
      ];
      const csv = csvRows.map(r => r.map(c => `"${String(c || '').replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = currentQuery ? `companies_${currentQuery}.csv` : 'companies.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      setError('Export failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fade in timeout={500}>
      <Box sx={{ p: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <SearchHistory
            searchHistory={searchHistory}
            onHistoryClick={handleHistoryClick}
            onClearHistory={handleClearHistory}
            onDeleteHistoryItem={handleDeleteHistoryItem}
            selectedHistoryIndex={selectedHistoryIndex}
            setSelectedHistoryIndex={setSelectedHistoryIndex}
          />
        </Box>

        {/* Filter Panel */}
        <Box sx={{ mb: 1 }}>
          <CompanyFilterPanel
            companyFilters={companyFilters}
            setCompanyFilters={setCompanyFilters}
            onApplyFilters={() => {
              setCurrentQuery(companyFilters.query);
              loadPageData(0, rowsPerPage, true);
            }}
            isLoading={isPageLoading}
          />
        </Box>

        <TableContainer component={Paper} sx={{ position: 'relative' }}>
          {/* ── Toolbar ── */}
          <Box sx={{
            p: { xs: 1.5, sm: 2 },
            display: 'flex',
            gap: { xs: 1, sm: 1.5, md: 2 },
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: { xs: 'wrap', lg: 'nowrap' },
            borderBottom: '1px solid rgba(0,0,0,0.12)',
          }}>
            {/* Expand All / Collapse All Links */}
            <Box sx={{
              flexShrink: 0,
              minWidth: { xs: '100%', sm: 'auto' },
              order: { xs: 3, lg: 1 },
              display: 'flex',
              gap: 1.5,
              alignItems: 'center'
            }}>
              <Typography
                onClick={() => !allExpanded && toggleAll()}
                sx={{
                  fontSize: '0.875rem',
                  color: !allExpanded ? '#1976d2' : '#999',
                  textDecoration: !allExpanded ? 'underline' : 'none',
                  cursor: !allExpanded ? 'pointer' : 'default',
                  fontWeight: !allExpanded ? 500 : 400,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    color: !allExpanded ? '#1565c0' : '#999',
                    textDecorationThickness: !allExpanded ? '2px' : '0px',
                  },
                  pointerEvents: allExpanded ? 'none' : 'auto',
                  userSelect: 'none'
                }}
              >
                Expand All
              </Typography>
              <Typography sx={{ color: '#ccc', fontSize: '0.875rem', userSelect: 'none' }}>|</Typography>
              <Typography
                onClick={() => allExpanded && toggleAll()}
                sx={{
                  fontSize: '0.875rem',
                  color: allExpanded ? '#1976d2' : '#999',
                  textDecoration: allExpanded ? 'underline' : 'none',
                  cursor: allExpanded ? 'pointer' : 'default',
                  fontWeight: allExpanded ? 500 : 400,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    color: allExpanded ? '#1565c0' : '#999',
                    textDecorationThickness: allExpanded ? '2px' : '0px',
                  },
                  pointerEvents: !allExpanded ? 'none' : 'auto',
                  userSelect: 'none'
                }}
              >
                Collapse All
              </Typography>
            </Box>

            <TextField
              placeholder="Search company, category, sector…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: '200px', lg: '250px' }, order: { xs: 1, lg: 2 }, '& .MuiOutlinedInput-root': { height: '40px' } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />

            <TablePagination
              rowsPerPageOptions={[15, 25]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              showFirstButton showLastButton
              labelDisplayedRows={({ from, to, count }) => {
                if (hasMore && to === count) return `${from}-${to} of many`;
                return `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`;
              }}
              sx={{
                flexShrink: 0, order: { xs: 3, lg: 3 }, minWidth: { xs: '100%', sm: 'auto' },
                borderRadius: '4px', backgroundColor: '#fff', overflow: 'hidden',
                '& .MuiTablePagination-toolbar': { minHeight: '38px', height: '38px', padding: 0, paddingLeft: { xs: 1, sm: 1.5 }, paddingRight: { xs: 0.5, sm: 1 } },
              }}
            />

            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
              disabled={loading}
              sx={{ flexShrink: 0, height: '40px', minWidth: { xs: '100%', sm: 'auto' }, order: { xs: 4, lg: 4 }, textTransform: 'none' }}
            >
              Export All
            </Button>
          </Box>

          {isPageLoading && (
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.8)', zIndex: 10 }}>
              <CircularProgress />
            </Box>
          )}

          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                {/* Expand toggle column */}
                <TableCell style={{ width: 50 }} sx={{ padding: '6px' }} />

                <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', textAlign: 'left', width: '22%', pl: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <TableSortLabel active={true} direction={orderBy === 'companyName' ? order : 'asc'} onClick={() => handleSort('companyName')}>
                      Company Name
                    </TableSortLabel>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <Select
                        value={companyType}
                        onChange={(e) => { setCompanyType(e.target.value); setPage(0); }}
                        sx={{ fontSize: '0.75rem', height: '28px', backgroundColor: '#fff', borderRadius: 1, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ddd' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#999' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' } }}
                      >
                        <MenuItem value="all" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>All</MenuItem>
                        <MenuItem value="converged" sx={{ fontSize: '0.75rem' }}>Convergence</MenuItem>
                        <MenuItem value="non_converged" sx={{ fontSize: '0.75rem' }}>Non-Convergence</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </TableCell>

                <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', textAlign: 'left', width: '10%' }}>
                  <TableSortLabel active={true} direction={orderBy === 'categoryName' ? order : 'asc'} onClick={() => handleSort('categoryName')}>Category Name</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', textAlign: 'left', width: '10%' }}>
                  <TableSortLabel active={true} direction={orderBy === 'sectorName' ? order : 'asc'} onClick={() => handleSort('sectorName')}>Sector Name</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', textAlign: 'center', width: '12%' }}>
                  <TableSortLabel active={true} direction={orderBy === 'counts' ? order : 'asc'} onClick={() => handleSort('counts')}>Count</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', textAlign: 'center', width: '12%' }}>
                  <TableSortLabel active={true} direction={orderBy === 'disposedCount' ? order : 'asc'} onClick={() => handleSort('disposedCount')}>Disposed</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', textAlign: 'center', width: '12%' }}>
                  <TableSortLabel active={true} direction={orderBy === 'avgDisposalDays' ? order : 'asc'} onClick={() => handleSort('avgDisposalDays')}>Avg Disposal time</TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', textAlign: 'center', width: '8%' }}>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedData.length > 0 ? (
                sortedData.map((row, index) => {
                  const key = rowKey(row, index);
                  const isExpanded = expandedRows.has(key);
                  const hasCats = Array.isArray(row.categories) && row.categories.length > 0;
                  const hasFb = Object.values(row.feedbackDistribution || {}).some(v => v > 0);
                  const canExpand = hasCats || hasFb;

                  return (
                    <React.Fragment key={key}>
                      <TableRow
                        sx={{
                          '&:hover': { backgroundColor: '#f5f5f5' },
                          '&:nth-of-type(odd)': { backgroundColor: isExpanded ? '#f0f0ff' : '#fafafa' },
                          backgroundColor: isExpanded ? '#f0f0ff !important' : undefined,
                          transition: 'background-color 0.2s',
                        }}
                      >
                        {/* Expand icon */}
                        <TableCell sx={{ width: 50, padding: '6px' }}>
                          {canExpand && (
                            <IconButton
                              size="small"
                              onClick={() => toggleRow(key)}
                              sx={{
                                color: isExpanded ? 'rgba(94,79,170,1)' : 'text.secondary',
                                transition: 'transform 0.2s',
                                transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                              }}
                            >
                              <ArrowDownIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>

                        <TableCell sx={{ textAlign: 'left', fontSize: '0.95rem', pl: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 500 }}>{row.companyName}</span>
                            {companyType === 'all' && (
                              <Chip
                                label={row._type === 'converged' ? 'Conv' : 'Non-Conv'}
                                size="small"
                                sx={{
                                  height: '16px', fontSize: '0.6rem', fontWeight: 700,
                                  backgroundColor: row._type === 'converged' ? '#e8f5e9' : '#fff3e0',
                                  color: row._type === 'converged' ? '#2e7d32' : '#e65100',
                                  border: `1px solid ${row._type === 'converged' ? '#a5d6a7' : '#ffcc80'}`,
                                  '& .MuiChip-label': { px: '4px' },
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>

                        <TableCell sx={{ textAlign: 'left', fontSize: '0.95rem' }}>{row.categoryName}</TableCell>
                        <TableCell sx={{ textAlign: 'left', fontSize: '0.95rem' }}>{row.sectorName}</TableCell>
                        <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '0.95rem', color: '#000' }}>
                          {(row.count || row.counts || 0).toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', fontSize: '0.95rem', color: '#000' }}>
                          {(row.disposedCount || 0).toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', fontSize: '0.95rem', fontWeight: 'bold', color: row.avgDisposalDays <= 15 ? '#2e7d32' : row.avgDisposalDays <= 30 ? '#ed6c02' : '#d32f2f' }}>
                          {row.avgDisposalDays != null ? `${row.avgDisposalDays.toFixed(1)} d` : '—'}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Tooltip
                            title="You will be redirected to the Map View page"
                            arrow
                            placement="top"
                          >
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<VisibilityIcon />}
                              onClick={() => {
                                setViewSnackbarOpen(true);
                                setTimeout(() => navigate('/semantic-search', { state: { companyName: row.companyName, fromCompanies: true } }), 1200);
                              }}
                              sx={{ textTransform: 'none', fontSize: '0.75rem', height: '28px', borderColor: 'rgba(94,79,170,0.5)', color: 'rgba(94,79,170,1)', '&:hover': { borderColor: 'rgba(94,79,170,1)', backgroundColor: 'rgba(94,79,170,0.05)' } }}
                            >
                              View
                            </Button>
                          </Tooltip>
                        </TableCell>
                      </TableRow>

                      {/* ── Expand Panel ── */}
                      {canExpand && (
                        <TableRow sx={{ '& > td': { p: 0, border: 0 } }}>
                          <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                            <Collapse in={isExpanded} timeout={250} unmountOnExit>
                              <ExpandPanel row={row} />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 3 }}>
                    {isPageLoading ? 'Loading companies…' : 'No companies found'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Redirect notification */}
        <Snackbar
          open={viewSnackbarOpen}
          autoHideDuration={1200}
          onClose={() => setViewSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="info" variant="filled" sx={{ width: '100%', fontWeight: 500 }}>
            Redirecting you to the Map View page…
          </Alert>
        </Snackbar>
      </Box>
    </Fade>
  );
};

export default Companies;